import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LogIn, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createRoom, getRoom } from "@/lib/api";
import { TopBar } from "@/components/TopBar";

const RoleToggle = ({ role, setRole }) => (
  <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-900/60 p-1" data-testid="role-toggle">
    {[
      { v: "es", label: "Hablo español" },
      { v: "ar", label: "نهضر بالدارجة", ar: true },
    ].map((o) => (
      <button
        key={o.v}
        type="button"
        data-testid={`role-${o.v}-button`}
        onClick={() => setRole(o.v)}
        className={`h-12 rounded-xl text-base font-bold transition-colors ${o.ar ? "font-arabic text-lg" : ""} ${
          role === o.v ? "bg-amber-500 text-slate-950" : "text-slate-300 hover:bg-slate-800"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export default function RoomLobby() {
  const nav = useNavigate();
  const [role, setRole] = useState("es");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const room = await createRoom();
      nav(`/sala/${room.code}?rol=${role}`);
    } catch {
      toast.error("No se pudo crear la sala.");
    } finally {
      setBusy(false);
    }
  };

  const join = async (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (c.length !== 6) return toast.error("El código tiene 6 caracteres.");
    setBusy(true);
    try {
      await getRoom(c);
      nav(`/sala/${c}?rol=${role}`);
    } catch {
      toast.error("Sala no encontrada. Revisa el código.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain flex h-full flex-col bg-[#0b132b] text-white" data-testid="room-lobby-page">
      <TopBar title="Varios dispositivos · sala" />
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-8 px-5 py-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">Paso 1</p>
          <h1 className="mt-2 text-2xl font-extrabold tracking-tight">¿Qué idioma hablas tú?</h1>
          <div className="mt-4">
            <RoleToggle role={role} setRole={setRole} />
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">Paso 2</p>
          <h2 className="mt-2 text-lg font-bold">Crea una sala nueva</h2>
          <button
            type="button"
            data-testid="create-room-button"
            onClick={create}
            disabled={busy}
            className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 text-lg font-bold shadow-lg shadow-blue-600/30 transition-colors hover:bg-blue-500 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
            Crear sala y mostrar QR
          </button>
          <p className="mt-2 text-xs text-slate-400">Las demás personas escanean el QR con la cámara de su móvil, eligen su idioma y entran. Sin límite de dispositivos por sala.</p>
        </div>

        <form onSubmit={join}>
          <h2 className="text-lg font-bold">…o únete con un código</h2>
          <div className="mt-3 flex flex-col gap-3">
            <input
              data-testid="join-room-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
              placeholder="ABC123"
              autoCapitalize="characters"
              className="h-14 w-full rounded-2xl border border-slate-600 bg-slate-900/70 px-4 text-center font-mono text-2xl font-bold tracking-[0.3em] text-white placeholder:text-slate-600 focus:border-amber-400 focus:outline-none"
            />
            <button
              type="submit"
              data-testid="join-room-button"
              disabled={busy}
              className="mx-auto flex h-14 w-48 items-center justify-center gap-2 rounded-2xl bg-amber-500 px-8 font-bold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-60"
            >
              <LogIn className="h-5 w-5" /> Entrar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
